import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JOURNEYS = ["Hourly Rental", "Long Distance Journey", "Local Intercity", "Airport Transfer", "Tour Package"];
const EMPTY = {
  name: "",
  mobile: "",
  pickupLocation: "",
  pickupDate: "",
  dropLocation: "",
  dropDate: "",
  journeyType: "",
};

const inputCls =
  "w-full bg-ink/70 border border-white/15 focus:border-signal px-4 py-3 text-sm text-bone placeholder:text-zinc-600 outline-none transition-colors duration-200";
const labelCls = "block text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-2";
const errCls = "text-[11px] font-mono text-signal mt-1.5";

export default function BookingModal({ open, journey, onClose }) {
  const [fields, setFields] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [reference, setReference] = useState("");

  useEffect(() => {
    if (open) {
      setFields(journey ? { ...EMPTY, journeyType: journey } : EMPTY);
      setErrors({});
      setServerError("");
      setReference("");
      setLoading(false);
    }
  }, [open, journey]);

  const set = (k) => (e) => {
    setFields((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const today = new Date().toISOString().slice(0, 10);

  const waHref = reference
    ? `https://wa.me/919827953314?text=${encodeURIComponent(
        `New booking — Aircon Travel\n\nName: ${fields.name}\nMobile: ${fields.mobile}\nJourney: ${fields.journeyType}\nPickup: ${fields.pickupLocation} on ${fields.pickupDate}\nDrop: ${fields.dropLocation} on ${fields.dropDate}\nRef: ${reference.slice(-8).toUpperCase()}`
      )}`
    : "#";

  const validate = () => {
    const er = {};
    if (fields.name.trim().length < 2) er.name = "Please enter your full name";
    const digits = fields.mobile.replace(/\D/g, "").slice(-10);
    if (!/^[6-9]\d{9}$/.test(digits)) er.mobile = "Enter a valid 10-digit mobile number";
    if (!fields.pickupLocation.trim()) er.pickupLocation = "Pickup location is required";
    if (!fields.pickupDate) er.pickupDate = "Pickup date is required";
    if (!fields.dropLocation.trim()) er.dropLocation = "Drop location is required";
    if (!fields.dropDate) er.dropDate = "Drop date is required";
    if (!fields.journeyType) er.journeyType = "Choose the nature of the journey";
    return er;
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = validate();
    if (Object.keys(er).length) {
      setErrors(er);
      return;
    }
    setLoading(true);
    setServerError("");
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        name: fields.name.trim(),
        mobile: fields.mobile.replace(/\D/g, "").slice(-10),
        pickup_location: fields.pickupLocation.trim(),
        pickup_date: fields.pickupDate,
        drop_location: fields.dropLocation.trim(),
        drop_date: fields.dropDate,
        journey_type: fields.journeyType,
      });
      setReference(data.id);
    } catch (err) {
      setServerError(
        err?.response?.status === 422
          ? "Please check the details — every field is required."
          : "Could not reach the dispatch desk. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="booking-modal"
        className="max-w-lg bg-surface border border-white/15 p-8 md:p-10 max-h-[90vh] overflow-y-auto"
      >
        <DialogTitle className="sr-only">Book your ride</DialogTitle>

        <AnimatePresence mode="wait">
          {reference ? (
            <motion.div
              key="success"
              data-testid="booking-success"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-8"
            >
              <CheckCircle2 size={56} className="text-signal mx-auto" strokeWidth={1.5} />
              <h3 className="font-display uppercase text-3xl mt-5">Ride requested</h3>
              <p className="text-zinc-400 text-sm mt-3">
                Booking reference{" "}
                <span className="font-mono text-signal">{reference.slice(-8).toUpperCase()}</span>
              </p>
              <p className="text-zinc-400 text-sm mt-1">
                We will call you on your mobile to confirm the ride.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
                <a
                  data-testid="booking-whatsapp-btn"
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-ink font-mono text-xs uppercase tracking-[0.22em] px-8 py-4 hover:bg-bone transition-colors duration-200"
                >
                  Send booking on WhatsApp
                </a>
                <button
                  data-testid="booking-success-close-btn"
                  onClick={onClose}
                  className="inline-flex items-center justify-center border border-white/25 text-bone font-mono text-xs uppercase tracking-[0.22em] px-8 py-4 hover:border-signal hover:text-signal transition-colors duration-200"
                >
                  Done
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onSubmit={submit}
              noValidate
            >
              <div className="mb-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal mb-2">
                  Booking window
                </p>
                <h3 className="font-display uppercase text-3xl leading-none">Book your ride</h3>
              </div>

              <div className="grid gap-5">
                <div>
                  <label className={labelCls} htmlFor="bk-name">Full name</label>
                  <input id="bk-name" data-testid="booking-name-input" className={inputCls} placeholder="Your name"
                    value={fields.name} onChange={set("name")} />
                  {errors.name && <p className={errCls} data-testid="booking-name-error">{errors.name}</p>}
                </div>

                <div>
                  <label className={labelCls} htmlFor="bk-mobile">Mobile number</label>
                  <input id="bk-mobile" data-testid="booking-mobile-input" className={inputCls}
                    placeholder="10-digit mobile" inputMode="numeric" value={fields.mobile} onChange={set("mobile")} />
                  {errors.mobile && <p className={errCls} data-testid="booking-mobile-error">{errors.mobile}</p>}
                </div>

                <div>
                  <label className={labelCls} htmlFor="bk-journey">Nature of the journey</label>
                  <select id="bk-journey" data-testid="booking-journey-select" className={`${inputCls} appearance-none cursor-pointer ${fields.journeyType ? "" : "text-zinc-600"}`}
                    value={fields.journeyType} onChange={set("journeyType")}>
                    <option value="" disabled>Choose a journey type</option>
                    {JOURNEYS.map((j) => (
                      <option key={j} value={j} className="bg-surface text-bone">{j}</option>
                    ))}
                  </select>
                  {errors.journeyType && <p className={errCls} data-testid="booking-journey-error">{errors.journeyType}</p>}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls} htmlFor="bk-pickup-loc">Pickup location</label>
                    <input id="bk-pickup-loc" data-testid="booking-pickup-location-input" className={inputCls}
                      placeholder="City / address" value={fields.pickupLocation} onChange={set("pickupLocation")} />
                    {errors.pickupLocation && <p className={errCls} data-testid="booking-pickup-location-error">{errors.pickupLocation}</p>}
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="bk-pickup-date">Pickup date</label>
                    <input id="bk-pickup-date" data-testid="booking-pickup-date-input" type="date" min={today}
                      className={`${inputCls} [color-scheme:dark]`} value={fields.pickupDate} onChange={set("pickupDate")} />
                    {errors.pickupDate && <p className={errCls} data-testid="booking-pickup-date-error">{errors.pickupDate}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls} htmlFor="bk-drop-loc">Drop location</label>
                    <input id="bk-drop-loc" data-testid="booking-drop-location-input" className={inputCls}
                      placeholder="City / address" value={fields.dropLocation} onChange={set("dropLocation")} />
                    {errors.dropLocation && <p className={errCls} data-testid="booking-drop-location-error">{errors.dropLocation}</p>}
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="bk-drop-date">Drop date</label>
                    <input id="bk-drop-date" data-testid="booking-drop-date-input" type="date" min={today}
                      className={`${inputCls} [color-scheme:dark]`} value={fields.dropDate} onChange={set("dropDate")} />
                    {errors.dropDate && <p className={errCls} data-testid="booking-drop-date-error">{errors.dropDate}</p>}
                  </div>
                </div>

                {serverError && (
                  <p className="font-mono text-xs text-signal border border-signal/40 bg-signal/10 px-4 py-3" data-testid="booking-server-error">
                    {serverError}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="booking-submit-btn"
                  disabled={loading}
                  className="mt-2 w-full inline-flex items-center justify-center gap-3 bg-signal text-ink font-mono text-xs uppercase tracking-[0.22em] px-8 py-4 hover:bg-bone transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 size={15} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    "Confirm Booking"
                  )}
                </button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
